import { z } from 'zod';

/*
 * Short-lived browser state for the athlete account flows (PLA-1655).
 *
 * Both records last seven days, as on iOS, and never hold a password, a verification code or
 * contact details beyond the email the visitor typed into this browser. Storage that cannot be
 * read or written behaves as empty: the flows still work, they just do not survive a reload.
 */

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const VERIFICATION_KEY = 'playerup.athlete-web.pending-verification';
const JOIN_KEY = 'playerup.athlete-web.pending-join';

function read<T>(key: string, schema: z.ZodType<T & { savedAt: number }>, now = Date.now()) {
  try {
    const parsed = schema.safeParse(JSON.parse(localStorage.getItem(key) ?? 'null'));
    if (!parsed.success) return null;
    if (now - parsed.data.savedAt > SEVEN_DAYS_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function write(key: string, value: object | null) {
  try {
    if (value) localStorage.setItem(key, JSON.stringify({ ...value, savedAt: Date.now() }));
    else localStorage.removeItem(key);
  } catch {
    /* The flow continues for this page view. */
  }
}

// --- Pending verification ----------------------------------------------------------------------

const verificationSchema = z.object({ email: z.string().min(1), savedAt: z.number() });

/** The email awaiting its 6-digit code, so the visitor can return without retyping it. */
export function pendingVerification(): string | null {
  return read(VERIFICATION_KEY, verificationSchema)?.email ?? null;
}

export function rememberPendingVerification(email: string | null) {
  write(VERIFICATION_KEY, email ? { email: email.trim() } : null);
}

// --- Pending team join -------------------------------------------------------------------------

const pendingJoinSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('teamCode'),
    teamCode: z.string().regex(/^[A-Z0-9]{6}$/),
    joinSource: z.enum(['manual_code', 'smart_link']),
    teamId: z.string().nullish(),
    teamName: z.string().nullish(),
    organizationName: z.string().nullish(),
    sport: z.string().nullish(),
    savedAt: z.number(),
  }),
  z.object({
    kind: z.literal('invitation'),
    invitationId: z.string().min(1),
    teamId: z.string().nullish(),
    teamName: z.string().nullish(),
    sport: z.string().nullish(),
    savedAt: z.number(),
  }),
  z.object({
    kind: z.literal('shareable'),
    shareableId: z.string().min(1),
    teamId: z.string().min(1),
    teamName: z.string().nullish(),
    sport: z.string().nullish(),
    savedAt: z.number(),
  }),
]);
export type PendingJoin = z.infer<typeof pendingJoinSchema>;
export type PendingJoinInput = PendingJoin extends infer Join
  ? Join extends { savedAt: number }
    ? Omit<Join, 'savedAt'>
    : never
  : never;

/** A team the visitor chose before signing in; it is joined once, after sign-in and setup. */
export function pendingJoin(): PendingJoin | null {
  return read(JOIN_KEY, pendingJoinSchema);
}

export function rememberPendingJoin(join: PendingJoinInput | null) {
  write(JOIN_KEY, join);
}

// --- Optional setup steps (PLA-1705) ----------------------------------------------------------

const OPTIONAL_SETUP_KEY = 'playerup.athlete-web.optional-setup';

/*
 * Set once the required setup is saved and cleared when the optional steps end, so `/home/setup`
 * keeps showing them although the athlete now has a name. Per tab: another tab, or a later visit,
 * opens the profile instead.
 */
export function optionalSetupPending(userId: string): boolean {
  try {
    return sessionStorage.getItem(OPTIONAL_SETUP_KEY) === userId;
  } catch {
    return false;
  }
}

export function rememberOptionalSetup(userId: string | null) {
  try {
    if (userId) sessionStorage.setItem(OPTIONAL_SETUP_KEY, userId);
    else sessionStorage.removeItem(OPTIONAL_SETUP_KEY);
  } catch {
    /* Without storage the steps still run; a reload then opens the profile. */
  }
}

/** Sign-out discards these, so the next person on this browser does not inherit them. */
export function forgetAthleteFlowState() {
  write(VERIFICATION_KEY, null);
  write(JOIN_KEY, null);
  rememberOptionalSetup(null);
}
